import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Users } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useCreateVendor, useVendors } from '@/features/ap/queries'
import type { Vendor, VendorPayload } from '@/features/ap/types'
import { CountryCombobox } from '@/components/CountryCombobox'
import { CurrencyCombobox } from '@/components/CurrencyCombobox'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'

function blank(country: string, currency: string): VendorPayload {
  return {
    display_name: '',
    email: '',
    phone: '',
    country,
    default_currency: currency,
    default_payment_terms_days: 30,
  }
}

export function VendorsListPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('invoicing.vendors.manage')
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data, isLoading } = useVendors()
  const createMut = useCreateVendor()
  const regional = useRegionalDefaults()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<VendorPayload>(() => blank(regional.country, regional.currency))
  // Snap defaults once tenant Regional Settings finish loading.
  const regionalSynced = useRef(false)
  useEffect(() => {
    if (!regional.isLoaded || regionalSynced.current) return
    setForm((prev) => ({
      ...prev,
      country: prev.country || regional.country,
      default_currency: prev.default_currency || regional.currency,
    }))
    regionalSynced.current = true
  }, [regional.isLoaded, regional.country, regional.currency])

  const rows = data?.vendors ?? []

  const columns = useMemo(() => [
    { accessorKey: 'display_name', header: 'Vendor',
      cell: ({ row }: { row: { original: Vendor } }) => (
        <div>
          <Link to={`/invoicing/vendors/${row.original.id}`} className="font-medium text-primary hover:underline">
            {row.original.display_name}
          </Link>
          {row.original.legal_name ? <div className="text-xs text-muted-foreground">{row.original.legal_name}</div> : null}
        </div>
      ),
    },
    { accessorKey: 'email', header: 'Email' },
    { accessorKey: 'country', header: 'Country' },
    { accessorKey: 'default_currency', header: 'Currency' },
    { accessorKey: 'is_active', header: 'Status',
      cell: ({ row }: { row: { original: Vendor } }) =>
        row.original.is_active ? <Badge variant="success">active</Badge> : <Badge variant="secondary">inactive</Badge>,
    },
    { accessorKey: 'actions', header: '',
      cell: ({ row }: { row: { original: Vendor } }) => (
        <Button size="sm" variant="ghost" onClick={() => navigate(`/invoicing/bills/new?vendor_id=${row.original.id}`)}>
          New bill
        </Button>
      ),
    },
  ], [navigate])

  function setField<K extends keyof VendorPayload>(k: K, v: VendorPayload[K]) {
    setForm((prev) => ({ ...prev, [k]: v }))
  }

  async function onCreate() {
    if (!form.display_name?.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' }); return
    }
    try {
      await createMut.mutateAsync(form)
      toast({ title: 'Vendor created' })
      setOpen(false)
      setForm(blank(regional.country, regional.currency))
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed', variant: 'destructive' })
    }
  }

  return (
    <Page
      title="Vendors"
      description="The suppliers and contractors you pay. Bills link back to vendors for GL + payment reconciliation."
      actions={canManage ? (
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-1 size-4" /> New vendor
        </Button>
      ) : undefined}
    >
      {isLoading ? (
        <Card><div className="flex justify-center py-16"><LoadingSpinner /></div></Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={Users}
            title="No vendors yet"
            description="Add your first vendor to start entering bills."
            action={canManage ? <Button onClick={() => setOpen(true)}><Plus className="mr-1 size-4" /> New vendor</Button> : null}
          />
        </Card>
      ) : (
        <Card><div className="p-3"><DataTable columns={columns as never} rows={rows} /></div></Card>
      )}

      <Modal open={open} onOpenChange={setOpen} title="New vendor">
        <div className="space-y-3">
          <FormField label="Display name" required value={form.display_name} onChange={e => setField('display_name', e.currentTarget.value)} />
          <FormField label="Legal name" value={form.legal_name || ''} onChange={e => setField('legal_name', e.currentTarget.value)} />
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Email" type="email" value={form.email || ''} onChange={e => setField('email', e.currentTarget.value)} />
            <FormField label="Phone" value={form.phone || ''} onChange={e => setField('phone', e.currentTarget.value)} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <FormField
              label="Country"
              control={<CountryCombobox value={form.country || null} onChange={v => setField('country', v || '')} onCurrencyChange={c => setField('default_currency', c)} />}
            />
            <FormField
              label="Default currency"
              control={<CurrencyCombobox value={form.default_currency || regional.currency} onChange={v => setField('default_currency', v)} />}
            />
          </div>
          <FormField
            label="Default payment terms (days)"
            type="number"
            value={String(form.default_payment_terms_days ?? 30)}
            onChange={e => setField('default_payment_terms_days', Number(e.currentTarget.value) || 0)}
          />
          <FormField
            label="Default expense account code"
            value={form.default_expense_account_code || ''}
            onChange={e => setField('default_expense_account_code', e.currentTarget.value)}
            hint="Pre-fills the line-item account on new bills for this vendor."
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={createMut.isPending}>Create vendor</Button>
          </div>
        </div>
      </Modal>
    </Page>
  )
}
